// apps/merchant-v3/src/components/security/ImpersonateBanner.tsx
//
// Phase 6 Wave 11 Task 36 — Top sticky banner shown ONLY when an admin
// is currently impersonating the merchant.
// Reads `useMe().impersonatedBy` (the canonical /auth/me payload now
// carries this field — see types/auth.ts MeUser.impersonatedBy).
//
// Yellow banner (sticky in flow at the top of AppShell, before any
// other banner) carries:
//   - copy: "You are viewing as {{merchant}} (Admin {{admin}})"
//   - "Stop impersonating" button → POST /api/v1/admin/impersonate/stop.
//     On success the page reloads to refresh the auth cookie back to
//     the admin identity.
//
// vg-binding: PLAN:task-36
// vg-binding: P6.D-128
// vg-binding: P6.D-130
// vg-binding: G-58

import { useState, type ReactElement } from 'react';
import { Eye, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { apiClient, ApiError } from '@/lib/api/client';
import { useMe } from '@/lib/auth/useMe';

/** POST /api/v1/admin/impersonate/stop — server clears the
 *  impersonation cookie and rotates the JWT back to the admin
 *  identity. The FE then forces a hard reload so every cached query
 *  re-fetches under the admin context. */
async function stopImpersonating(): Promise<void> {
  await apiClient.post<unknown>('/api/v1/admin/impersonate/stop', {});
}

export function ImpersonateBanner(): ReactElement | null {
  const me = useMe();
  const { t } = useTranslation('common');
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const impersonator = me.data?.impersonatedBy;
  if (!impersonator) return null;

  const merchantName = me.data?.name ?? me.data?.email ?? '';
  const adminName = impersonator.adminName ?? impersonator.adminId ?? '';

  const onStop = async (): Promise<void> => {
    setStopping(true);
    setError(null);
    try {
      await stopImpersonating();
      // Hard reload — drops every TanStack Query cache + Zustand
      // store entry so the next paint is rendered under the admin
      // identity. Falls back to /admin if the BE rotated the cookie
      // for an admin-only domain.
      if (typeof window !== 'undefined') {
        window.location.href = '/';
      }
    } catch (err) {
      const apiErr = err instanceof ApiError ? err : null;
      setError(
        apiErr?.userMessage ??
          apiErr?.message ??
          t('errors.generic', { defaultValue: 'Something went wrong.' }),
      );
      setStopping(false);
    }
  };

  return (
    <div
      role="alert"
      data-testid="impersonate-banner"
      className="sticky top-0 z-30 flex flex-col gap-2 border-b border-amber-300 bg-amber-100 px-4 py-2 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex items-start gap-2">
        <Eye className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden />
        <p className="font-medium">
          {t('settings.impersonate.banner', {
            defaultValue:
              'You are viewing as {{merchant}} (Admin {{admin}})',
            merchant: merchantName,
            admin: adminName,
          })}
        </p>
      </div>

      <div className="flex items-center gap-3">
        {error ? (
          <span
            data-testid="impersonate-banner-error"
            className="text-xs text-red-700"
          >
            {error}
          </span>
        ) : null}
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="impersonate-stop-button"
          disabled={stopping}
          onClick={() => void onStop()}
          className="border-amber-400 bg-pw-card text-amber-900 hover:bg-amber-50"
        >
          {stopping ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : null}
          <span>
            {t('settings.impersonate.stop', {
              defaultValue: 'Stop impersonating',
            })}
          </span>
        </Button>
      </div>
    </div>
  );
}

export default ImpersonateBanner;
